import {
  Alert,
  AlertTrigger,
  schAloftArea,
  schSiteRating,
  schSixTierQuality,
} from '@aloft/toolkit-shared';

import { z } from 'zod';

import { PropertyGridCellKey } from '../../../../components/PropertyGrid/types.ts';
import {
  CompPropertyDataState,
  PropertyDataState,
  SubjectPropertyDataState,
} from '../../../../redux/types.ts';
import { AlertDefinition } from '../../types.ts';

const ALERT_TRIGGERS: AlertTrigger[] = [
  'navigate/market',
  'navigate/cost-approach',
  'navigate/adjustments',
  'navigate/commentary',
  'navigate/reconciliation',
];

const schParkingSpaces = z
  .object({
    spaces: z.number().int().nonnegative().optional(),
    hasFeature: z.boolean(),
  })
  .optional()
  .superRefine((data, ctx) => {
    if (data?.hasFeature && data?.spaces === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Required',
        path: ['spaces'],
      });
    }
  });

const schSalesComparablesImprovementFields = z.object({
  gla: schAloftArea,
  bedrooms: z.number().int().nonnegative(),
  fullBaths: z.number().int().nonnegative(),
  halfBaths: z.number().int().nonnegative(),
  age: z.number().int().nonnegative(),
  uadQuality: schSixTierQuality,
  uadCondition: schSixTierQuality,
  garage: schParkingSpaces,
  detachedGarage: schParkingSpaces,
  carport: schParkingSpaces,
});

const schSalesComparablesFields = z.object({
  id: z.string(),
  site: z.object({
    area: schAloftArea,
    influence: z.string(),
    influenceRating: schSiteRating,
    view: z.string(),
    viewRating: schSiteRating,
  }),
  improvements: schSalesComparablesImprovementFields.extend({
    arv: schSalesComparablesImprovementFields.optional(),
  }),
});

const GARAGE_FIELD_PRIORITY = ['garage', 'detachedGarage', 'carport'] as const;

const issueToCellKey = (id: string, issue: z.ZodIssue): PropertyGridCellKey | undefined => {
  const path = issue.path as (string | number)[];
  if (!path.length) return undefined;
  return `${id}-editableData.${path.join('.')}` as PropertyGridCellKey;
};

const getTargetCellKeys = (id: string, issues: z.ZodIssue[]): PropertyGridCellKey[] => [
  ...new Set(
    issues
      .map((issue) => issueToCellKey(id, issue))
      .filter((key): key is PropertyGridCellKey => key !== undefined),
  ),
];

const getFocusCellKey = (id: string, issues: z.ZodIssue[]): PropertyGridCellKey | undefined => {
  // Priority: find first failing parking spaces issue in order garage -> detachedGarage -> carport
  const garageField = GARAGE_FIELD_PRIORITY.find((field) =>
    issues.some(
      (issue) =>
        issue.path[0] === 'improvements' && issue.path[1] === field && issue.path[2] === 'spaces',
    ),
  );
  if (garageField) {
    return `${id}-editableData.improvements.${garageField}.spaces` as PropertyGridCellKey;
  }
  return getTargetCellKeys(id, issues)[0];
};

const mapPropertyDataEntityToSalesComparableFieldsSchema = (
  propertyDataEntity: PropertyDataState,
) => {
  if (propertyDataEntity.id === 'subject') {
    const { editableData } = (propertyDataEntity as SubjectPropertyDataState).subjectData;
    const arvImprovements = editableData.arv
      ? {
          gla: editableData.arv?.improvements?.gla,
          bedrooms: editableData.arv?.improvements?.bedrooms,
          fullBaths: editableData.arv?.improvements?.fullBaths,
          halfBaths: editableData.arv?.improvements?.halfBaths,
          age: editableData.improvements?.age,
          uadQuality: editableData.arv?.improvements?.uadQuality,
          uadCondition: editableData.arv?.improvements?.uadCondition,
          garage: editableData.arv?.improvements?.garage,
          detachedGarage: editableData.arv?.improvements?.detachedGarage,
          carport: editableData.arv?.improvements?.carport,
        }
      : undefined;
    return schSalesComparablesFields.safeParse({
      id: propertyDataEntity.id,
      improvements: {
        gla: editableData.improvements?.gla,
        bedrooms: editableData.improvements?.bedrooms,
        fullBaths: editableData.improvements?.fullBaths,
        halfBaths: editableData.improvements?.halfBaths,
        age: editableData.improvements?.age,
        uadQuality: editableData.improvements?.uadQuality,
        uadCondition: editableData.improvements?.uadCondition,
        garage: editableData.improvements?.garage,
        detachedGarage: editableData.improvements?.detachedGarage,
        carport: editableData.improvements?.carport,
        arv: arvImprovements,
      },
      site: {
        area: editableData.site?.area,
        influence: editableData.site?.influence,
        influenceRating: editableData.site?.influenceRating,
        view: editableData.site?.view,
        viewRating: editableData.site?.viewRating,
      },
    });
  }
  const { editableData } = (propertyDataEntity as CompPropertyDataState).compData;

  return schSalesComparablesFields.safeParse({
    id: propertyDataEntity.id,
    improvements: {
      gla: editableData.improvements?.gla,
      bedrooms: editableData.improvements?.bedrooms,
      fullBaths: editableData.improvements?.fullBaths,
      halfBaths: editableData.improvements?.halfBaths,
      age: editableData.improvements?.age,
      uadQuality: editableData.improvements?.uadQuality,
      uadCondition: editableData.improvements?.uadCondition,
      garage: editableData.improvements?.garage,
      detachedGarage: editableData.improvements?.detachedGarage,
      carport: editableData.improvements?.carport,
    },
    site: {
      area: editableData.site?.area,
      influence: editableData.site?.influence,
      influenceRating: editableData.site?.influenceRating,
      view: editableData.site?.view,
      viewRating: editableData.site?.viewRating,
    },
  });
};

export const missingPropertyData: AlertDefinition = ({
  propertyDataEntities,
  compSequence,
  propertyType,
  compType,
  fullCompSequence,
}) => {
  const errors = ['subject', ...compSequence]
    .map((id) => {
      if (propertyType && id === 'subject' && propertyType !== 'subject') {
        return undefined;
      }
      if (propertyType && id !== 'subject' && propertyType !== 'comp') {
        return undefined;
      }
      const propertyDataEntity = propertyDataEntities[id];
      if (!propertyDataEntity) {
        return undefined;
      }

      const result = mapPropertyDataEntityToSalesComparableFieldsSchema(propertyDataEntity);
      if (result.success) {
        return undefined;
      }

      const propertyName =
        id === 'subject' ? 'Subject' : `Comp ${(fullCompSequence?.indexOf(id) ?? 0) + 1}`;

      const targetCellKey = getFocusCellKey(id, result.error.issues);
      const targetCellKeys = getTargetCellKeys(id, result.error.issues);

      return {
        type: 'missingPropertyData',
        status: 'active',
        severity: 'error',
        orderType: ['appraisify'],
        message: `${propertyName} has missing data.`,
        propertyID: id,
        ...(compType ? { compType } : {}),
        fixingInfo: {
          tips: 'One or more of the rows for the property is incomplete.',
          pairedCostRouteItem: 'features',
          fixingMethod: ['action'],
          commentAlwaysRequired: false,
          targetCellKey,
          targetCellKeys,
        },
        triggers: ALERT_TRIGGERS,
      } as Alert;
    })
    .filter((alert): alert is Alert => alert !== undefined);

  return errors;
};
